import { useRef, useState } from "react";

import { type DataPathToken } from "@doitintl/cloudflow-commons";
import { ModelType } from "@doitintl/cmp-models";
import DoneIcon from "@mui/icons-material/Done";
import EditIcon from "@mui/icons-material/Edit";
import { Box, IconButton, Menu, Stack, TextField } from "@mui/material";

import { useToggle } from "../../../../../../../utils/useToggle";
import { ellipsisSx } from "./ReferencedFieldChip";
import { CUSTOM_TOKEN_PLACEHOLDER } from "./useReferencedFieldMenuItems";

export type ReferencedFieldChipCustomTokenProps = {
  token: DataPathToken;
  onChange: (value: string) => void;
};

export const ReferencedFieldChipCustomToken = ({ token, onChange }: ReferencedFieldChipCustomTokenProps) => {
  const boxEl = useRef<HTMLDivElement>(null);
  const [isEditing, open, close] = useToggle();

  const [value, setValue] = useState(token.member === CUSTOM_TOKEN_PLACEHOLDER ? "" : token.member);

  const closeMenuAndDispatchOnChange = () => {
    close();
    const newValue = value.trim() === "" ? CUSTOM_TOKEN_PLACEHOLDER : value;
    if (newValue !== token.member) {
      onChange(newValue);
    }
  };

  return (
    <>
      <Stack
        ref={boxEl}
        direction="row"
        alignItems="center"
        onClick={(event) => {
          event.stopPropagation();
          event.preventDefault();
          open();
        }}
        sx={{
          maxWidth: "50%",
          color: token.member === CUSTOM_TOKEN_PLACEHOLDER ? "text.secondary" : "primary.main",
        }}
        tabIndex={-1}
      >
        <Box sx={ellipsisSx}>.{token.member}</Box>
        <EditIcon sx={{ fontSize: "14px" }} />
      </Stack>
      <Menu
        anchorEl={boxEl.current}
        open={isEditing}
        onClose={closeMenuAndDispatchOnChange}
        onClick={(event) => {
          event.stopPropagation();
        }}
        disableRestoreFocus
        slotProps={{
          paper: {
            sx: {
              mt: 1,
            },
          },
          root: {
            sx: ({ zIndex }) => ({
              zIndex: zIndex.tooltip + 1, // INFO: ensures that the menu is above the tooltip, not ideal but works for now
            }),
          },
        }}
      >
        <TextField
          label={(token.memberModel.type === ModelType.MAP && token.memberModel.keyMemberName) || "Key"}
          value={value}
          onChange={(event) => {
            setValue(event.target.value);
          }}
          size="small"
          sx={{
            mx: 1,
          }}
          onKeyUp={(event) => {
            if (event.key === "Enter") {
              closeMenuAndDispatchOnChange();
            }
          }}
        />
        <IconButton onClick={closeMenuAndDispatchOnChange} color="primary" sx={{ mr: 1 }}>
          <DoneIcon />
        </IconButton>
      </Menu>
    </>
  );
};
